import { ISO_COUNTRIES, isoCountryName } from "@libs/countries-contracts/isoCountries";
import type { IsoCountry } from "@libs/countries-contracts/isoCountries";
import { buyerSchema } from "./buyer";
import type { Buyer, BuyerField, BuyerPatch } from "./buyer";
import {
  cityLabel,
  countryData,
  diallingCode,
  displayPhone,
  matchRegion,
  nationalPhone,
  postcodeNoun,
  readPhone,
  readPostcode,
  regionFits,
  regionLabel,
  regionOptions,
  withPostcodeNoun,
} from "@libs/countries-contracts/world";

/** Required profile fields for a checkout. Email is verified separately;
 * payment credentials never belong in this declaration or profile. */
export type BuyerFieldDefinition = {
  id: BuyerField;
  label: string;
  autoComplete: string;
  /** `choice`: one of `allowedValues`, picked from a short list (a title).
   *  `region`: one of `options`, the country's regions, picked from a long
   *  list (a US state). */
  type: "text" | "tel" | "country" | "choice" | "region";
  allowedValues?: readonly string[];
  /** The values a country field lists where they are more than it takes
   *  (`allowedValues`): a merch store's, which lists every country and
   *  delivers only to the UK. A value outside `allowedValues` can be chosen
   *  and is kept on the form, but never completes the checkout, which says
   *  why (`listedCountries`). Clients that do not know it list
   *  `allowedValues`, as before. */
  listedValues?: readonly string[];
  /** The country a form starts on, for app builds that know no better
   *  (`legacyRequirements`); every other client starts on the buyer's own
   *  (`defaultBuyerCountry`). */
  defaultValue?: string;
  /** The buyer may leave it empty: a second address line, an optional
   *  region. Its label says so (`fieldLabel`). Explicit false requires a
   *  shown region even where the country's postal service does not. */
  optional?: boolean;
  /** A region's choices, as the buyer's country lists them. */
  options?: readonly { value: string; label: string }[];
  /** A phone asked with the country it is dialled in, chosen beside it
   *  (`buyerFieldsFor`); without, a number as typed, read in the address
   *  country. */
  withCountry?: boolean;
};
export type BuyerRequirements = {
  fields: readonly BuyerFieldDefinition[];
  /** The form asks by the buyer's country (`buyerFieldsFor`): what a
   *  client gets when it says it can draw it (`fields=by-country`). Without
   *  it, every declared field is asked as declared, as before. */
  byCountry?: boolean;
};

/** The fields a form asks: by the buyer's country where its requirements
 *  say so, else as declared. */
export const askedFields = (requirements: BuyerRequirements, country: string | null | undefined) =>
  requirements.byCountry ? buyerFieldsFor(requirements, country) : [...requirements.fields];

/** A field's label as a form shows it: "(optional)" after one the buyer may
 *  leave empty. */
export const fieldLabel = (field: Pick<BuyerFieldDefinition, "label" | "optional">) =>
  field.optional ? `${field.label} (optional)` : field.label;

/**
 * The fields a checkout asks a buyer in `country` for, as Google's address
 * metadata has that country's addresses (`countries.json`): its postcode
 * named as there (ZIP code, Eircode) and required as the seller declared
 * it, even where the country's post needs none: the sellers' forms require
 * one (Skiddle's, See Tickets', Eventbrite's card), so an Irish address
 * gives its Eircode, and one in a country with no postcodes (the UAE) what
 * its buyer uses, under the field's own label; a
 * region only where they have one that is required or chosen from a list
 * (a US state, an Irish county, never an English county), named as there;
 * a town named as there (an Australian suburb); a second address line
 * always optional. Every other field is as the vendor declared it. Without a
 * country, or one the file does not know, the vendor's fields as declared,
 * with no region.
 */
export function buyerFieldsFor(
  { fields }: BuyerRequirements,
  country: string | null | undefined,
): BuyerFieldDefinition[] {
  const data = countryData(country);
  return fields.flatMap((field): BuyerFieldDefinition[] => {
    switch (field.id) {
      case "postcode": {
        if (!data) return [field];
        if (!data.postcode) return [field];
        return [{ ...field, label: withPostcodeNoun(field.label, postcodeNoun(country)) }];
      }
      case "region": {
        const region = data?.region;
        if (!region || (!region.required && !region.options)) return [];
        return [
          {
            ...field,
            label: regionLabel(region.kind),
            // A seller may require a county even when the postal service
            // does not (Ticketmaster Ireland's billing form).
            optional: field.optional ?? !region.required,
            ...(region.options
              ? {
                  type: "region" as const,
                  options: regionOptions(country) ?? [],
                }
              : { type: "text" as const }),
          },
        ];
      }
      case "city": {
        const label = data ? cityLabel(country) : undefined;
        return [label ? { ...field, label } : field];
      }
      case "address2":
        return [{ ...field, optional: true }];
      case "phone":
        return [{ ...field, withCountry: true }];
      default:
        return [field];
    }
  });
}

/** The fields a form asks by the buyer's country (`buyerFieldsFor`) knows:
 *  what a client says it can draw when it asks for a checkout's details
 *  (`fields=by-country`). */
export const BY_COUNTRY = "by-country";

/**
 * A checkout's details as an app build from before forms asked by country
 * can draw them: no second line or region (fields it does not know, which
 * it could not read), and the country field starting on the United Kingdom,
 * as it always did. Its buyers' addresses are those it can give: a start
 * never requires a region (`buyerSchemaFor`).
 */
export function legacyRequirements<T extends BuyerRequirements>(requirements: T): T {
  return {
    ...requirements,
    fields: requirements.fields
      // A build from before them also draws every country field as the
      // address country: a nationality is not asked of it.
      .filter((field) => field.id !== "address2" && field.id !== "region" && field.id !== "nationality")
      .map((field) => (field.type === "country" ? { ...field, defaultValue: "GB" } : field)),
  };
}

/** A region's name: the name of one the buyer's country lists ("US-CA" is
 *  California), a region typed by hand where it lists none as typed, and
 *  none for a region that is not the country's (one saved in another). */
export function regionName(country: string | null | undefined, region: string | null | undefined): string | undefined {
  if (!region || !regionFits(country, region)) return undefined;
  return regionOptions(country)?.find((option) => option.value === region)?.label ?? region;
}

/** A whole name split at its last space, as a suggestion for the first and
 *  last name: "Mary Jane Smith" is "Mary Jane" and "Smith"; one word is a
 *  first name only. */
export function splitWholeName(name: string): {
  firstName?: string;
  lastName?: string;
} {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { firstName: words[0] };
  return {
    firstName: words.slice(0, -1).join(" "),
    lastName: words[words.length - 1],
  };
}

/** Whether the form's first and last name are a suggestion, split from a
 *  profile's whole name (`prefillBuyerDetails`), for the buyer to check
 *  before they are saved: the checkout asks for them, and the profile has
 *  neither, only a whole name. */
export function suggestsBuyerNames(
  { fields }: BuyerRequirements,
  saved: Partial<Buyer>,
): saved is Partial<Buyer> & { name: string } {
  return (
    Boolean(saved.name) &&
    saved.firstName === undefined &&
    saved.lastName === undefined &&
    fields.some((field) => field.id === "firstName" || field.id === "lastName")
  );
}

/** Where the buyer seems to be, for a form that has no country of theirs:
 *  the visitor's country on Link in Bio, the app's region in the app. */
export type BuyerCountrySignal = { country?: string | null };

/**
 * The country a country field starts on for a buyer who saved none: where
 * they seem to be (`signal`), where the field lists it; else the field's
 * own default (an app build's, `legacyRequirements`); else the one country
 * it takes, where it takes one (Ticketmaster's United Kingdom); else none,
 * and the buyer chooses.
 */
export function defaultBuyerCountry(
  field: BuyerFieldDefinition,
  signal?: BuyerCountrySignal,
): string | undefined {
  const seen = signal?.country?.trim().toUpperCase();
  const listed = field.listedValues ?? field.allowedValues;
  if (seen && isoCountryName(seen) && (!listed || listed.includes(seen))) return seen;
  if (field.defaultValue) return field.defaultValue;
  return field.allowedValues?.length === 1 ? field.allowedValues[0] : undefined;
}

/** Defaults fill missing values, never replace something the buyer saved.
 *  The country starts where the buyer seems to be (`defaultBuyerCountry`).
 *  A profile saved with only a whole name offers it split
 *  (`splitWholeName`) for a checkout's first and last name: a suggestion on
 *  the form, which stays incomplete until the buyer saves them
 *  (`parseBuyerDetails` reads the saved profile, not this). A saved phone is
 *  shown as it is dialled in its country (07700 900123 with the United
 *  Kingdom chosen beside it), its country the one it was saved with, else
 *  read in the address country, as it always was; with none, its country is
 *  the address country. */
export function prefillBuyerDetails(
  { fields, byCountry }: BuyerRequirements,
  saved: Partial<Buyer>,
  signal?: BuyerCountrySignal,
): Partial<Buyer> {
  // Keyed by field id: a default is a value the field's schema takes.
  const details: Partial<Record<keyof Buyer, string>> = { ...saved };
  const ids = new Set(fields.map((field) => field.id));
  if (suggestsBuyerNames({ fields }, saved)) {
    const suggested = splitWholeName(saved.name);
    if (ids.has("firstName") && suggested.firstName)
      details.firstName = suggested.firstName;
    if (ids.has("lastName") && suggested.lastName)
      details.lastName = suggested.lastName;
  }
  for (const field of fields) {
    if (details[field.id] !== undefined) continue;
    // Only the address country is guessed; a nationality is the buyer's to give.
    const value = byCountry && field.id === "country" ? defaultBuyerCountry(field, signal) : field.defaultValue;
    if (value !== undefined) details[field.id] = value;
  }
  // A region saved in another country is none of this one's.
  if (byCountry && details.region && !regionFits(details.country, details.region)) delete details.region;
  if (byCountry && ids.has("phone")) {
    const dialled = saved.phone ? readPhone(saved.phone, saved.phoneCountry ?? details.country) : null;
    if (dialled) {
      details.phone = nationalPhone(dialled);
      details.phoneCountry = dialled.country;
    } else details.phoneCountry ??= details.country;
  }
  return details as Partial<Buyer>;
}

/** A form's country changed: the phone's country follows it while there is
 *  no number yet, and a region chosen in the old country goes. */
export function withBuyerCountry(value: Partial<Buyer>, country: string): Partial<Buyer> {
  const { region, ...rest } = value;
  return {
    ...rest,
    country,
    ...(region && regionFits(country, region) ? { region } : {}),
    ...(value.phone?.trim() ? {} : { phoneCountry: country }),
  };
}

/** A phone number typed: one typed with + and its calling code takes that
 *  country with it ("+353…" is Ireland's, whatever was chosen beside it). */
export function withBuyerPhone(value: Partial<Buyer>, phone: string): Partial<Buyer> {
  const dialled = /^\s*(?:\+|00)/.test(phone) ? readPhone(phone, value.phoneCountry ?? value.country) : null;
  return { ...value, phone, ...(dialled ? { phoneCountry: dialled.country } : {}) };
}

const blank = (value: string | null | undefined) => !value?.trim();

/** A field's value as saved: its details (a phone is two), or `invalid`.
 *  By the buyer's country, a postcode must be one of theirs and a phone a
 *  whole one there; as declared, any text the schema takes. */
function readField(
  field: BuyerFieldDefinition,
  value: Partial<Buyer>,
  byCountry: boolean,
): Partial<Buyer> | "invalid" {
  const raw = value[field.id];
  if (byCountry) switch (field.id) {
    case "phone": {
      const dialled = readPhone(raw ?? "", value.phoneCountry ?? value.country);
      return dialled && buyerSchema.shape.phone.safeParse(dialled.e164).success
        ? { phone: dialled.e164, phoneCountry: dialled.country }
        : "invalid";
    }
    case "postcode": {
      const postcode = readPostcode(raw!, value.country);
      return postcode !== null && buyerSchema.shape.postcode.safeParse(postcode).success ? { postcode } : "invalid";
    }
    case "region": {
      if (!field.options) break;
      // By its value ("US-CA"), its code alone ("CA") or its name.
      const region = matchRegion(field.options, raw!);
      return region !== null ? { region } : "invalid";
    }
    default:
      // Any other: as declared, below.
      break;
  }
  const result = buyerSchema.shape[field.id].safeParse(raw);
  if (!result.success || result.data === undefined) return "invalid";
  if (field.allowedValues && !field.allowedValues.includes(result.data)) return "invalid";
  return { [field.id]: result.data };
}

/** Validate and select only what this checkout needs, as the buyer's
 * country has it (`buyerFieldsFor`). Unrelated saved data must neither
 * block checkout nor be overwritten by a partial form. The provider still
 * validates its full contract on the server before execution.
 *
 * `data` is what the buyer gave, a phone E.164 with its country; `patch`
 * saves it, and forgets what the checkout asked for that is now empty (a
 * second line cleared) or that the country's addresses do not have (a
 * postcode, for an address moved to the UAE). */
export function parseBuyerDetails(
  requirements: BuyerRequirements,
  value: Partial<Buyer>,
):
  | { success: true; data: Partial<Buyer>; patch: BuyerPatch }
  | { success: false; missing: BuyerField[] } {
  const byCountry = requirements.byCountry === true;
  const fields = askedFields(requirements, value.country);
  const data: Partial<Buyer> = {};
  // Never the country: a declared country is never optional or hidden.
  const patch: Partial<Record<BuyerField, null>> = {};
  const missing: BuyerField[] = [];
  for (const field of fields) {
    // A declared field is required, including one a profile may lack,
    // unless the buyer may leave it empty.
    if (blank(value[field.id])) {
      if (field.optional) patch[field.id] = null;
      else missing.push(field.id);
      continue;
    }
    const read = readField(field, value, byCountry);
    if (read === "invalid") missing.push(field.id);
    else Object.assign(data, read);
  }
  if (missing.length) return { success: false, missing };
  const shown = new Set(fields.map((field) => field.id));
  for (const field of requirements.fields) if (!shown.has(field.id)) patch[field.id] = null;
  return { success: true, data, patch: { ...patch, ...data } as BuyerPatch };
}

/** What is wrong with a field the buyer filled in, in their words: a
 *  postcode that is not one of their country's, a phone number that is not
 *  a whole one. None for an empty field, or one that is right. */
export function buyerFieldProblems(
  requirements: BuyerRequirements,
  value: Partial<Buyer>,
): Partial<Record<BuyerField, string>> {
  const problems: Partial<Record<BuyerField, string>> = {};
  if (!requirements.byCountry) return problems;
  for (const field of buyerFieldsFor(requirements, value.country)) {
    if (blank(value[field.id]) || readField(field, value, true) !== "invalid") continue;
    if (field.id === "postcode") {
      const noun = postcodeNoun(value.country);
      const example = countryData(value.country)?.postcode?.example;
      const article = /^[aeiou]/i.test(noun) ? "an" : "a";
      problems.postcode = example ? `Enter ${article} ${noun} like ${example}.` : `Check your ${noun}.`;
    }
    if (field.id === "phone") {
      const country = value.phoneCountry ?? value.country;
      const name = country ? isoCountryName(country) : undefined;
      problems.phone = name
        ? `Enter a full ${name} (${diallingCode(country)}) number.`
        : "Choose the number's country.";
    }
  }
  return problems;
}

/** The declared fields a checkout still asks a saved profile's buyer for:
 *  those its prefilled form (`prefillBuyerDetails`) lacks or holds invalid,
 *  as the buyer's country has them: the form's country (`country`) where the
 *  buyer chose another there (a US address asks a saved UK one for its state
 *  and ZIP code). The rest are summarised (`buyerDetailSummary`). A default
 *  (a country) fills its field on the form and is still asked for: the buyer
 *  has not given it. */
export function missingBuyerFields(
  requirements: BuyerRequirements,
  saved: Partial<Buyer>,
  signal?: BuyerCountrySignal,
  country?: string,
): BuyerField[] {
  const prefilled = prefillBuyerDetails(requirements, saved, signal);
  const form = country === undefined ? prefilled : { ...prefilled, country };
  const parsed = parseBuyerDetails(requirements, form);
  const invalid = new Set(parsed.success ? [] : parsed.missing);
  return askedFields(requirements, form.country)
    .filter((field) => invalid.has(field.id) || (field.id === "country" && saved.country === undefined))
    .map((field) => field.id);
}

export const buyerCountryName = (value?: string) =>
  (value && isoCountryName(value)) ?? value ?? "";

/** Every country a buyer can give, by code. */
export const buyerCountryCodes: readonly string[] = ISO_COUNTRIES.map(
  (country) => country.code,
);

/** The countries a country field offers, by name: those it allows, else
 *  every country. */
export function buyerCountries(field: BuyerFieldDefinition): IsoCountry[] {
  const allowed = field.allowedValues && new Set(field.allowedValues);
  return ISO_COUNTRIES.filter((country) => !allowed || allowed.has(country.code));
}

/** The countries a country field lists, by name: `listedValues` where it
 *  has them (every country, for a merch store that delivers only to the
 *  UK), else those it allows (`buyerCountries`). */
export function listedCountries(field: BuyerFieldDefinition): IsoCountry[] {
  if (!field.listedValues) return buyerCountries(field);
  const listed = new Set(field.listedValues);
  return ISO_COUNTRIES.filter((country) => listed.has(country.code));
}

/** The countries a phone can be dialled in, by name: every country with a
 *  calling code. */
export const phoneCountries: readonly IsoCountry[] = ISO_COUNTRIES.filter((country) =>
  Boolean(countryData(country.code)?.phone),
);

/** The fields that make up an address. */
const ADDRESS_FIELDS: ReadonlySet<BuyerField> = new Set(["address", "address2", "city", "region", "postcode", "country"]);

/**
 * What the details section shows, on the web and in the app alike: what the
 * profile already gives (`known`), summarised under one Edit unless the
 * buyer is editing, and the fields asked under it. A field the buyer may
 * leave empty and has not filled (a second address line) is asked with the
 * rest of the address when any of it is, and otherwise waits for Edit.
 * Saved values decide which optional fields are asked, so typing does not
 * hide them; the summary still reflects edits when the buyer presses Done.
 */
export function buyerDetailsShown({
  fields,
  missing,
  value,
  saved = value,
  editing,
}: {
  fields: readonly BuyerFieldDefinition[];
  missing: readonly BuyerField[];
  value: Partial<Buyer>;
  saved?: Partial<Buyer>;
  editing: boolean;
}): { known: BuyerFieldDefinition[]; asked: BuyerFieldDefinition[]; summarised: boolean } {
  // Its address being asked for: a phone asked for alone asks no line 2.
  const asking = fields.some((field) => missing.includes(field.id) && ADDRESS_FIELDS.has(field.id));
  const wanted = fields.filter((field) =>
    missing.includes(field.id) || (asking && field.optional === true && blank(saved[field.id])),
  );
  const known = fields.filter((field) =>
    !wanted.includes(field) && !(field.optional === true && blank(value[field.id])),
  );
  const summarised = known.length > 0 && !editing;
  return {
    known,
    summarised,
    asked: summarised ? wanted : [...fields],
  };
}

/** Both renderers summarise only the declared fields, with address parts
 * together in the order an address is written. A phone-only checkout shows
 * no saved address or empty lines. */
export function buyerDetailSummary(
  { fields }: BuyerRequirements,
  value: Partial<Buyer>,
) {
  const ids = new Set(fields.map((field) => field.id));
  const address = (["address", "address2", "city", "region", "postcode", "country"] as const)
    .filter((id) => ids.has(id))
    .map((id) =>
      id === "country"
        ? buyerCountryName(value[id])
        : id === "region"
          ? regionName(value.country, value.region)
          : value[id],
    )
    .filter(Boolean)
    .join(", ");
  // The title and names a checkout asks for apart, as one. A saved title
  // still appears when the names are missing and asked for separately.
  const name = (["title", "firstName", "lastName"] as const)
    .filter((id) => ids.has(id))
    .map((id) => value[id])
    .filter(Boolean)
    .join(" ");
  const dialled = value.phone ? readPhone(value.phone, value.phoneCountry ?? value.country) : null;
  return [
    {
      key: "name",
      text: name,
      strong: true,
    },
    { key: "dateOfBirth", text: ids.has("dateOfBirth") && value.dateOfBirth ? `Date of birth: ${value.dateOfBirth}` : undefined, strong: false },
    { key: "birthYear", text: ids.has("birthYear") && value.birthYear ? `Birth year: ${value.birthYear}` : undefined, strong: false },
    { key: "gender", text: ids.has("gender") && value.gender ? `Gender: ${value.gender}` : undefined, strong: false },
    { key: "nationality", text: ids.has("nationality") && value.nationality ? `Nationality: ${buyerCountryName(value.nationality)}` : undefined, strong: false },
    { key: "identityDocument", text: ids.has("identityDocument") && value.identityDocument ? `ID or passport: ${value.identityDocument}` : undefined, strong: false },
    { key: "address", text: address, strong: false },
    {
      key: "phone",
      text: ids.has("phone") ? (dialled ? displayPhone(dialled) : value.phone) : undefined,
      strong: false,
    },
  ].filter((line) => Boolean(line.text));
}
